import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Radio,
  Checkbox,
  Button,
  Progress,
  Divider,
  Row,
  Col,
  Typography,
  Space,
  Alert,
  message,
  Affix,
  Anchor,
  Tag,
  Breadcrumb,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SendOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  SafetyOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

interface DetailedAssessmentData {
  // 发行主体分析
  issuerAnalysis: {
    companyNameCN: string;
    companyNameEN: string;
    registeredAddress: string;
    registeredCapital: number;
    paidCapital: number;
    establishDate: string;
    creditCode: string;
    controllingShareholderName: string;
    controllingShareholderRatio: number;
    actualController: string;
    hasEquityPledge: boolean;
    pledgeRatio?: number;
  };

  // 财务状况分析
  financialAnalysis: {
    totalAssets: number;
    totalLiabilities: number;
    netAssets: number;
    revenue: number;
    netProfit: number;
    operatingCashFlow: number;
    debtRatio: number;
    currentRatio: number;
    roe: number;
    creditRating: string;
  };

  // 绿色项目评估
  greenProjectAssessment: {
    projectName: string;
    projectLocation: string;
    projectNature: string;
    totalInvestment: number;
    constructionPeriod: number;
    operationPeriod: number;
    greenStandards: string[];
    primaryCategory: string;
    secondaryCategory: string;
    annualEnergySaving?: number;
    annualCO2Reduction?: number;
    needThirdPartyVerification: boolean;
  };

  // 发行方案设计
  issuancePlan: {
    plannedIssuanceSize: string;
    plannedTerm: string;
    guaranteeStructure: string;
    targetExchange: string;
    expectedCouponRate?: number;
  };

  // 中介服务需求
  serviceRequirements: {
    underwriterPreference: string;
    legalCounselNeeds: string;
    ratingAgencyPreference: string;
    otherServiceNeeds: string[];
  };

  // 风险识别与应对
  riskManagement: {
    regulatoryApprovalStatus: string;
    environmentalCompliance: string;
    legalDisputeStatus: string;
    relatedPartyTransactions: string;
    externalGuarantees: string;
    assetMortgageStatus: string;
  };
}

const countFilledFields = (value: unknown): number => {
  if (Array.isArray(value)) {
    return value.reduce((sum, item) => sum + countFilledFields(item), 0);
  }
  if (value && typeof value === 'object') {
    return Object.values(value).reduce(
      (sum, item) => sum + countFilledFields(item),
      0
    );
  }
  if (typeof value === 'boolean') {
    return 1;
  }
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  return 1;
};

const OptimizedDetailedAssessment: React.FC = () => {
  const { projectCode } = useParams<{ projectCode: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.toLowerCase().startsWith('zh');

  // 状态管理
  const [completionProgress, setCompletionProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const remainingMinutes = Math.max(
    0,
    35 - Math.round(completionProgress * 0.35)
  );

  // 模块定义
  const assessmentModules = [
    {
      key: 'issuerAnalysis',
      title: t('assessment:page.moduleTitles.issuerAnalysis'),
      icon: <TeamOutlined />,
      description: t('assessment:page.moduleDescriptions.issuerAnalysis'),
      estimatedTime: t('assessment:page.estimatedDuration', { minutes: 8 }),
      color: '#10b981',
    },
    {
      key: 'financialAnalysis',
      title: t('assessment:page.moduleTitles.financialAnalysis'),
      description: t('assessment:page.moduleDescriptions.financialAnalysis'),
      estimatedTime: t('assessment:page.estimatedDuration', { minutes: 6 }),
      icon: <DollarOutlined />,
      color: '#3b82f6',
    },
    {
      key: 'greenProjectAssessment',
      title: t('assessment:page.moduleTitles.greenProject'),
      description: t('assessment:page.moduleDescriptions.greenProject'),
      estimatedTime: t('assessment:page.estimatedDuration', { minutes: 8 }),
      icon: <EnvironmentOutlined />,
      color: '#059669',
    },
    {
      key: 'issuancePlan',
      title: t('assessment:page.moduleTitles.issuancePlan'),
      description: t('assessment:page.moduleDescriptions.issuancePlan'),
      estimatedTime: t('assessment:page.estimatedDuration', { minutes: 4 }),
      icon: <SettingOutlined />,
      color: '#7c3aed',
    },
    {
      key: 'serviceRequirements',
      title: t('assessment:page.moduleTitles.serviceRequirements'),
      description: t('assessment:page.moduleDescriptions.serviceRequirements'),
      estimatedTime: t('assessment:page.estimatedDuration', { minutes: 3 }),
      icon: <SafetyOutlined />,
      color: '#dc2626',
    },
    {
      key: 'riskManagement',
      title: t('assessment:page.moduleTitles.riskManagement'),
      description: t('assessment:page.moduleDescriptions.riskManagement'),
      estimatedTime: t('assessment:page.estimatedDuration', { minutes: 4 }),
      icon: <ExclamationCircleOutlined />,
      color: '#ea580c',
    },
  ];

  // 自动保存功能
  const autoSave = useCallback(
    async (values: Partial<DetailedAssessmentData>) => {
      try {
        // TODO: 调用API保存数据
        setLastSavedAt(new Date());
        const totalFields = 50;
        const filledFields = countFilledFields(values);
        setCompletionProgress(
          Math.min(100, Math.round((filledFields / totalFields) * 100))
        );
        console.log(t('assessment:message.autoSaveSuccess'), values);
      } catch (error) {
        console.error(t('assessment:message.saveRetryFailed'), error);
      }
    },
    [t]
  );

  // 表单值变化时自动保存
  const handleFormChange = useCallback(
    (
      _changedValues: Partial<DetailedAssessmentData>,
      allValues: Partial<DetailedAssessmentData>
    ) => {
      // 防抖保存
      setTimeout(() => {
        autoSave(allValues);
      }, 2000);
    },
    [autoSave]
  );

  // 保存草稿
  const handleSaveDraft = async () => {
    try {
      const values =
        (await form.validateFields()) as Partial<DetailedAssessmentData>;
      setIsLoading(true);

      // TODO: 调用API保存草稿
      await autoSave(values);
      message.success(t('assessment:message.saveSuccess'));
    } catch (error) {
      console.error(t('assessment:message.saveRetryFailed'), error);
      message.error(t('assessment:message.saveRetryFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // 提交评估
  const handleSubmitAssessment = async () => {
    try {
      const values =
        (await form.validateFields()) as Partial<DetailedAssessmentData>;
      setIsLoading(true);

      // TODO: 调用API提交评估
      console.log('Detailed assessment submission payload:', values);
      message.success(t('assessment:message.submitDetailedSuccess'));

      // 跳转回项目详情页
      navigate(`/admin/project-management/projects/${projectCode}`);
    } catch (error) {
      console.error(t('assessment:message.submitRetryFailed'), error);
      message.error(t('assessment:message.submitRetryFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // 渲染发行主体分析模块
  const renderIssuerAnalysis = () => (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TeamOutlined style={{ marginRight: '8px', color: '#10b981' }} />
          {t('project:taskContent.issuerAnalysis.title')}
          <Tag color="blue" style={{ marginLeft: '8px' }}>
            {t('project:taskContent.issuerAnalysis.estimated')}
          </Tag>
        </div>
      }
      style={{ marginBottom: '24px' }}
    >
      <Row gutter={[24, 16]}>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.companyNameCN.label'
            )}
            name={['issuerAnalysis', 'companyNameCN']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.companyNameCN.required'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'project:taskContent.issuerAnalysis.fields.companyNameCN.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.companyNameEN.label'
            )}
            name={['issuerAnalysis', 'companyNameEN']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.companyNameEN.required'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'project:taskContent.issuerAnalysis.fields.companyNameEN.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.registeredAddress.label'
            )}
            name={['issuerAnalysis', 'registeredAddress']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.registeredAddress.required'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'project:taskContent.issuerAnalysis.fields.registeredAddress.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.creditCode.label'
            )}
            name={['issuerAnalysis', 'creditCode']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.creditCode.required'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'project:taskContent.issuerAnalysis.fields.creditCode.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.registeredCapital.label'
            )}
            name={['issuerAnalysis', 'registeredCapital']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.registeredCapital.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.issuerAnalysis.fields.registeredCapital.placeholder'
              )}
              min={0}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.paidCapital.label'
            )}
            name={['issuerAnalysis', 'paidCapital']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.paidCapital.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.issuerAnalysis.fields.paidCapital.placeholder'
              )}
              min={0}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.establishDate.label'
            )}
            name={['issuerAnalysis', 'establishDate']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.establishDate.required'
                ),
              },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.issuerAnalysis.fields.establishDate.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.controllingShareholderName.label'
            )}
            name={['issuerAnalysis', 'controllingShareholderName']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.controllingShareholderName.required'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'project:taskContent.issuerAnalysis.fields.controllingShareholderName.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.controllingShareholderRatio.label'
            )}
            name={['issuerAnalysis', 'controllingShareholderRatio']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.controllingShareholderRatio.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.issuerAnalysis.fields.controllingShareholderRatio.placeholder'
              )}
              min={0}
              max={100}
              formatter={value => `${value}%`}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.actualController.label'
            )}
            name={['issuerAnalysis', 'actualController']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.actualController.required'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'project:taskContent.issuerAnalysis.fields.actualController.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuerAnalysis.fields.hasEquityPledge.label'
            )}
            name={['issuerAnalysis', 'hasEquityPledge']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuerAnalysis.fields.hasEquityPledge.required'
                ),
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{t('common:label.yes')}</Radio>
              <Radio value={false}>{t('common:label.no')}</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  // 渲染财务状况分析模块
  const renderFinancialAnalysis = () => (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <DollarOutlined style={{ marginRight: '8px', color: '#3b82f6' }} />
          {t('project:taskContent.financialAnalysis.title')}
          <Tag color="blue" style={{ marginLeft: '8px' }}>
            {t('project:taskContent.financialAnalysis.estimated')}
          </Tag>
        </div>
      }
      style={{ marginBottom: '24px' }}
    >
      <Row gutter={[24, 16]}>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.financialAnalysis.fields.totalAssets.label'
            )}
            name={['financialAnalysis', 'totalAssets']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.financialAnalysis.fields.totalAssets.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.financialAnalysis.fields.totalAssets.placeholder'
              )}
              min={0}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.financialAnalysis.fields.totalLiabilities.label'
            )}
            name={['financialAnalysis', 'totalLiabilities']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.financialAnalysis.fields.totalLiabilities.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.financialAnalysis.fields.totalLiabilities.placeholder'
              )}
              min={0}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.financialAnalysis.fields.netAssets.label'
            )}
            name={['financialAnalysis', 'netAssets']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.financialAnalysis.fields.netAssets.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.financialAnalysis.fields.netAssets.placeholder'
              )}
              min={0}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.financialAnalysis.fields.revenue.label'
            )}
            name={['financialAnalysis', 'revenue']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.financialAnalysis.fields.revenue.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.financialAnalysis.fields.revenue.placeholder'
              )}
              min={0}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.financialAnalysis.fields.netProfit.label'
            )}
            name={['financialAnalysis', 'netProfit']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.financialAnalysis.fields.netProfit.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.financialAnalysis.fields.netProfit.placeholder'
              )}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.financialAnalysis.fields.operatingCashFlow.label'
            )}
            name={['financialAnalysis', 'operatingCashFlow']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.financialAnalysis.fields.operatingCashFlow.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.financialAnalysis.fields.operatingCashFlow.placeholder'
              )}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.financialAnalysis.fields.debtRatio.label'
            )}
            name={['financialAnalysis', 'debtRatio']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.financialAnalysis.fields.debtRatio.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.financialAnalysis.fields.debtRatio.placeholder'
              )}
              min={0}
              max={100}
              formatter={value => `${value}%`}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.financialAnalysis.fields.currentRatio.label'
            )}
            name={['financialAnalysis', 'currentRatio']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.financialAnalysis.fields.currentRatio.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.financialAnalysis.fields.currentRatio.placeholder'
              )}
              min={0}
              step={0.01}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t('project:taskContent.financialAnalysis.fields.roe.label')}
            name={['financialAnalysis', 'roe']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.financialAnalysis.fields.roe.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.financialAnalysis.fields.roe.placeholder'
              )}
              min={0}
              formatter={value => `${value}%`}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.financialAnalysis.fields.creditRating.label'
            )}
            name={['financialAnalysis', 'creditRating']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.financialAnalysis.fields.creditRating.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.financialAnalysis.fields.creditRating.placeholder'
              )}
            >
              <Option value="AAA">AAA</Option>
              <Option value="AA+">AA+</Option>
              <Option value="AA">AA</Option>
              <Option value="AA-">AA-</Option>
              <Option value="A+">A+</Option>
              <Option value="A">A</Option>
              <Option value="A-">A-</Option>
              <Option value="BBB+">BBB+</Option>
              <Option value="BBB">BBB</Option>
              <Option value="other">
                {t(
                  'project:taskContent.financialAnalysis.fields.creditRating.options.other'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  // 渲染绿色项目评估模块
  const renderGreenProjectAssessment = () => (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <EnvironmentOutlined
            style={{ marginRight: '8px', color: '#059669' }}
          />
          {t('project:taskContent.greenProject.title')}
          <Tag color="blue" style={{ marginLeft: '8px' }}>
            {t('project:taskContent.greenProject.estimated')}
          </Tag>
        </div>
      }
      style={{ marginBottom: '24px' }}
    >
      <Row gutter={[24, 16]}>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.greenProject.fields.projectName.label'
            )}
            name={['greenProjectAssessment', 'projectName']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.greenProject.fields.projectName.required'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'project:taskContent.greenProject.fields.projectName.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.greenProject.fields.projectLocation.label'
            )}
            name={['greenProjectAssessment', 'projectLocation']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.greenProject.fields.projectLocation.required'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'project:taskContent.greenProject.fields.projectLocation.placeholder'
              )}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.greenProject.fields.projectNature.label'
            )}
            name={['greenProjectAssessment', 'projectNature']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.greenProject.fields.projectNature.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.greenProject.fields.projectNature.placeholder'
              )}
            >
              <Option value="new">
                {t(
                  'project:taskContent.greenProject.fields.projectNature.options.new'
                )}
              </Option>
              <Option value="expansion">
                {t(
                  'project:taskContent.greenProject.fields.projectNature.options.expansion'
                )}
              </Option>
              <Option value="renovation">
                {t(
                  'project:taskContent.greenProject.fields.projectNature.options.renovation'
                )}
              </Option>
              <Option value="acquisition">
                {t(
                  'project:taskContent.greenProject.fields.projectNature.options.acquisition'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.greenProject.fields.totalInvestment.label'
            )}
            name={['greenProjectAssessment', 'totalInvestment']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.greenProject.fields.totalInvestment.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.greenProject.fields.totalInvestment.placeholder'
              )}
              min={0}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.greenProject.fields.constructionPeriod.label'
            )}
            name={['greenProjectAssessment', 'constructionPeriod']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.greenProject.fields.constructionPeriod.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.greenProject.fields.constructionPeriod.placeholder'
              )}
              min={0}
              max={10}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.greenProject.fields.operationPeriod.label'
            )}
            name={['greenProjectAssessment', 'operationPeriod']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.greenProject.fields.operationPeriod.required'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.greenProject.fields.operationPeriod.placeholder'
              )}
              min={0}
              max={50}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={t(
              'project:taskContent.greenProject.fields.needThirdPartyVerification.label'
            )}
            name={['greenProjectAssessment', 'needThirdPartyVerification']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.greenProject.fields.needThirdPartyVerification.required'
                ),
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{t('common:label.yes')}</Radio>
              <Radio value={false}>{t('common:label.no')}</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label={t(
              'project:taskContent.greenProject.fields.greenStandards.label'
            )}
            name={['greenProjectAssessment', 'greenStandards']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.greenProject.fields.greenStandards.required'
                ),
              },
            ]}
          >
            <Checkbox.Group>
              <Row gutter={[8, 8]}>
                <Col span={8}>
                  <Checkbox value="pbc_catalog">
                    {t(
                      'project:taskContent.greenProject.fields.greenStandards.options.pbc'
                    )}
                  </Checkbox>
                </Col>
                <Col span={8}>
                  <Checkbox value="icma_gbp">
                    {t(
                      'project:taskContent.greenProject.fields.greenStandards.options.icma'
                    )}
                  </Checkbox>
                </Col>
                <Col span={8}>
                  <Checkbox value="cee_standard">
                    {t(
                      'project:taskContent.greenProject.fields.greenStandards.options.cee'
                    )}
                  </Checkbox>
                </Col>
                <Col span={8}>
                  <Checkbox value="eu_taxonomy">
                    {t(
                      'project:taskContent.greenProject.fields.greenStandards.options.eu'
                    )}
                  </Checkbox>
                </Col>
                <Col span={8}>
                  <Checkbox value="cbi_standard">
                    {t(
                      'project:taskContent.greenProject.fields.greenStandards.options.cbi'
                    )}
                  </Checkbox>
                </Col>
                <Col span={8}>
                  <Checkbox value="other_standard">
                    {t(
                      'project:taskContent.greenProject.fields.greenStandards.options.other'
                    )}
                  </Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.greenProject.fields.annualEnergySaving.label'
            )}
            name={['greenProjectAssessment', 'annualEnergySaving']}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.greenProject.fields.annualEnergySaving.placeholder'
              )}
              min={0}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.greenProject.fields.annualCO2Reduction.label'
            )}
            name={['greenProjectAssessment', 'annualCO2Reduction']}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.greenProject.fields.annualCO2Reduction.placeholder'
              )}
              min={0}
            />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  // 渲染发行方案设计模块
  const renderIssuancePlan = () => (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SettingOutlined style={{ marginRight: '8px', color: '#7c3aed' }} />
          {t('project:taskContent.issuancePlan.title')}
          <Tag color="blue" style={{ marginLeft: '8px' }}>
            {t('project:taskContent.issuancePlan.estimated')}
          </Tag>
        </div>
      }
      style={{ marginBottom: '24px' }}
    >
      <Row gutter={[24, 16]}>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuancePlan.fields.plannedIssuanceSize.label'
            )}
            name={['issuancePlan', 'plannedIssuanceSize']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuancePlan.fields.plannedIssuanceSize.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.issuancePlan.fields.plannedIssuanceSize.placeholder'
              )}
            >
              <Option value="usd_300m_500m">
                {t(
                  'project:taskContent.issuancePlan.fields.plannedIssuanceSize.options.rangeOne'
                )}
              </Option>
              <Option value="usd_500m_1b">
                {t(
                  'project:taskContent.issuancePlan.fields.plannedIssuanceSize.options.rangeTwo'
                )}
              </Option>
              <Option value="usd_over_1b">
                {t(
                  'project:taskContent.issuancePlan.fields.plannedIssuanceSize.options.rangeThree'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuancePlan.fields.plannedTerm.label'
            )}
            name={['issuancePlan', 'plannedTerm']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuancePlan.fields.plannedTerm.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.issuancePlan.fields.plannedTerm.placeholder'
              )}
            >
              <Option value="3y">
                {t(
                  'project:taskContent.issuancePlan.fields.plannedTerm.options.term3y'
                )}
              </Option>
              <Option value="5y">
                {t(
                  'project:taskContent.issuancePlan.fields.plannedTerm.options.term5y'
                )}
              </Option>
              <Option value="7y">
                {t(
                  'project:taskContent.issuancePlan.fields.plannedTerm.options.term7y'
                )}
              </Option>
              <Option value="10y">
                {t(
                  'project:taskContent.issuancePlan.fields.plannedTerm.options.term10y'
                )}
              </Option>
              <Option value="other">
                {t('project:taskContent.common.other')}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuancePlan.fields.guaranteeStructure.label'
            )}
            name={['issuancePlan', 'guaranteeStructure']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuancePlan.fields.guaranteeStructure.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.issuancePlan.fields.guaranteeStructure.placeholder'
              )}
            >
              <Option value="unsecured">
                {t(
                  'project:taskContent.issuancePlan.fields.guaranteeStructure.options.unsecured'
                )}
              </Option>
              <Option value="domestic_guarantee">
                {t(
                  'project:taskContent.issuancePlan.fields.guaranteeStructure.options.domesticGuarantee'
                )}
              </Option>
              <Option value="bank_sblc">
                {t(
                  'project:taskContent.issuancePlan.fields.guaranteeStructure.options.bankSBLC'
                )}
              </Option>
              <Option value="keepwell">
                {t(
                  'project:taskContent.issuancePlan.fields.guaranteeStructure.options.keepwell'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuancePlan.fields.targetExchange.label'
            )}
            name={['issuancePlan', 'targetExchange']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.issuancePlan.fields.targetExchange.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.issuancePlan.fields.targetExchange.placeholder'
              )}
            >
              <Option value="mox">
                {t(
                  'project:taskContent.issuancePlan.fields.targetExchange.options.mox'
                )}
              </Option>
              <Option value="hkex">
                {t(
                  'project:taskContent.issuancePlan.fields.targetExchange.options.hkex'
                )}
              </Option>
              <Option value="sgx">
                {t(
                  'project:taskContent.issuancePlan.fields.targetExchange.options.sgx'
                )}
              </Option>
              <Option value="other">
                {t('project:taskContent.common.other')}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.issuancePlan.fields.expectedCouponRate.label'
            )}
            name={['issuancePlan', 'expectedCouponRate']}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'project:taskContent.issuancePlan.fields.expectedCouponRate.placeholder'
              )}
              min={0}
              max={20}
              step={0.1}
              formatter={value => `${value}%`}
            />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  // 渲染中介服务需求模块
  const renderServiceRequirements = () => (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SafetyOutlined style={{ marginRight: '8px', color: '#dc2626' }} />
          {t('assessment:page.moduleTitles.serviceRequirements')}
          <Tag color="blue" style={{ marginLeft: '8px' }}>
            {t('assessment:page.estimatedDuration', { minutes: 3 })}
          </Tag>
        </div>
      }
      style={{ marginBottom: '24px' }}
    >
      <Row gutter={[24, 16]}>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.serviceRequirements.fields.underwriterPreference.label'
            )}
            name={['serviceRequirements', 'underwriterPreference']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.serviceRequirements.fields.underwriterPreference.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.serviceRequirements.fields.underwriterPreference.placeholder'
              )}
            >
              <Option value="international_ib">
                {t(
                  'project:taskContent.serviceRequirements.fields.underwriterPreference.options.international'
                )}
              </Option>
              <Option value="chinese_ib">
                {t(
                  'project:taskContent.serviceRequirements.fields.underwriterPreference.options.chinese'
                )}
              </Option>
              <Option value="mixed_syndicate">
                {t(
                  'project:taskContent.serviceRequirements.fields.underwriterPreference.options.mixed'
                )}
              </Option>
              <Option value="no_preference">
                {t(
                  'project:taskContent.serviceRequirements.fields.underwriterPreference.options.noPreference'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.serviceRequirements.fields.legalCounselNeeds.label'
            )}
            name={['serviceRequirements', 'legalCounselNeeds']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.serviceRequirements.fields.legalCounselNeeds.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.serviceRequirements.fields.legalCounselNeeds.placeholder'
              )}
            >
              <Option value="international_law_firm">
                {t(
                  'project:taskContent.serviceRequirements.fields.legalCounselNeeds.options.international'
                )}
              </Option>
              <Option value="local_law_firm">
                {t(
                  'project:taskContent.serviceRequirements.fields.legalCounselNeeds.options.local'
                )}
              </Option>
              <Option value="dual_counsel">
                {t(
                  'project:taskContent.serviceRequirements.fields.legalCounselNeeds.options.dual'
                )}
              </Option>
              <Option value="no_preference">
                {t(
                  'project:taskContent.serviceRequirements.fields.legalCounselNeeds.options.noPreference'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.serviceRequirements.fields.ratingAgencyPreference.label'
            )}
            name={['serviceRequirements', 'ratingAgencyPreference']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.serviceRequirements.fields.ratingAgencyPreference.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.serviceRequirements.fields.ratingAgencyPreference.placeholder'
              )}
            >
              <Option value="moodys">
                {t(
                  'project:taskContent.serviceRequirements.fields.ratingAgencyPreference.options.moodys'
                )}
              </Option>
              <Option value="sp">
                {t(
                  'project:taskContent.serviceRequirements.fields.ratingAgencyPreference.options.sp'
                )}
              </Option>
              <Option value="fitch">
                {t(
                  'project:taskContent.serviceRequirements.fields.ratingAgencyPreference.options.fitch'
                )}
              </Option>
              <Option value="ccxi">
                {t(
                  'project:taskContent.serviceRequirements.fields.ratingAgencyPreference.options.chengxin'
                )}
              </Option>
              <Option value="lianhe">
                {t(
                  'project:taskContent.serviceRequirements.fields.ratingAgencyPreference.options.lianhe'
                )}
              </Option>
              <Option value="no_preference">
                {t(
                  'project:taskContent.serviceRequirements.fields.ratingAgencyPreference.options.noPreference'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label={t(
              'project:taskContent.serviceRequirements.fields.otherServiceNeeds.label'
            )}
            name={['serviceRequirements', 'otherServiceNeeds']}
          >
            <Checkbox.Group>
              <Row gutter={[8, 8]}>
                <Col span={6}>
                  <Checkbox value="green_certification">
                    {t(
                      'project:taskContent.serviceRequirements.fields.otherServiceNeeds.options.greenCertification'
                    )}
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="financial_advisor">
                    {t(
                      'project:taskContent.serviceRequirements.fields.otherServiceNeeds.options.financialAdvisor'
                    )}
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="tax_advisor">
                    {t(
                      'project:taskContent.serviceRequirements.fields.otherServiceNeeds.options.taxAdvisor'
                    )}
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="esg_advisor">
                    {t(
                      'project:taskContent.serviceRequirements.fields.otherServiceNeeds.options.esgAdvisor'
                    )}
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="investor_relations">
                    {t(
                      'project:taskContent.serviceRequirements.fields.otherServiceNeeds.options.investorRelations'
                    )}
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="roadshow">
                    {t(
                      'project:taskContent.serviceRequirements.fields.otherServiceNeeds.options.roadshow'
                    )}
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="media_pr">
                    {t(
                      'project:taskContent.serviceRequirements.fields.otherServiceNeeds.options.mediaPr'
                    )}
                  </Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="other_service">
                    {t(
                      'project:taskContent.serviceRequirements.fields.otherServiceNeeds.options.otherService'
                    )}
                  </Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  // 渲染风险识别与应对模块
  const renderRiskManagement = () => (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ExclamationCircleOutlined
            style={{ marginRight: '8px', color: '#ea580c' }}
          />
          {t('project:taskContent.riskManagement.title')}
          <Tag color="blue" style={{ marginLeft: '8px' }}>
            {t('assessment:page.estimatedDuration', { minutes: 4 })}
          </Tag>
        </div>
      }
      style={{ marginBottom: '24px' }}
    >
      <Row gutter={[24, 16]}>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.riskManagement.fields.regulatoryApprovalStatus.label'
            )}
            name={['riskManagement', 'regulatoryApprovalStatus']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.riskManagement.fields.regulatoryApprovalStatus.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.riskManagement.fields.regulatoryApprovalStatus.placeholder'
              )}
            >
              <Option value="approved_all">
                {t(
                  'project:taskContent.riskManagement.fields.regulatoryApprovalStatus.options.approvedAll'
                )}
              </Option>
              <Option value="partial_pending">
                {t(
                  'project:taskContent.riskManagement.fields.regulatoryApprovalStatus.options.partialPending'
                )}
              </Option>
              <Option value="in_progress">
                {t(
                  'project:taskContent.riskManagement.fields.regulatoryApprovalStatus.options.inProgress'
                )}
              </Option>
              <Option value="not_started">
                {t(
                  'project:taskContent.riskManagement.fields.regulatoryApprovalStatus.options.notStarted'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.riskManagement.fields.environmentalCompliance.label'
            )}
            name={['riskManagement', 'environmentalCompliance']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.riskManagement.fields.environmentalCompliance.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.riskManagement.fields.environmentalCompliance.placeholder'
              )}
            >
              <Option value="full">
                {t(
                  'project:taskContent.riskManagement.fields.environmentalCompliance.options.full'
                )}
              </Option>
              <Option value="basic">
                {t(
                  'project:taskContent.riskManagement.fields.environmentalCompliance.options.basic'
                )}
              </Option>
              <Option value="minor_issue">
                {t(
                  'project:taskContent.riskManagement.fields.environmentalCompliance.options.minorIssue'
                )}
              </Option>
              <Option value="major_issue">
                {t(
                  'project:taskContent.riskManagement.fields.environmentalCompliance.options.majorIssue'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.riskManagement.fields.legalDisputeStatus.label'
            )}
            name={['riskManagement', 'legalDisputeStatus']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.riskManagement.fields.legalDisputeStatus.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.riskManagement.fields.legalDisputeStatus.placeholder'
              )}
            >
              <Option value="none">
                {t(
                  'project:taskContent.riskManagement.fields.legalDisputeStatus.options.none'
                )}
              </Option>
              <Option value="general">
                {t(
                  'project:taskContent.riskManagement.fields.legalDisputeStatus.options.general'
                )}
              </Option>
              <Option value="major">
                {t(
                  'project:taskContent.riskManagement.fields.legalDisputeStatus.options.major'
                )}
              </Option>
              <Option value="material_impact">
                {t(
                  'project:taskContent.riskManagement.fields.legalDisputeStatus.options.materialImpact'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.riskManagement.fields.relatedPartyTransactions.label'
            )}
            name={['riskManagement', 'relatedPartyTransactions']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.riskManagement.fields.relatedPartyTransactions.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.riskManagement.fields.relatedPartyTransactions.placeholder'
              )}
            >
              <Option value="none">
                {t(
                  'project:taskContent.riskManagement.fields.relatedPartyTransactions.options.none'
                )}
              </Option>
              <Option value="normal">
                {t(
                  'project:taskContent.riskManagement.fields.relatedPartyTransactions.options.normal'
                )}
              </Option>
              <Option value="many">
                {t(
                  'project:taskContent.riskManagement.fields.relatedPartyTransactions.options.many'
                )}
              </Option>
              <Option value="risky">
                {t(
                  'project:taskContent.riskManagement.fields.relatedPartyTransactions.options.risky'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.riskManagement.fields.externalGuarantees.label'
            )}
            name={['riskManagement', 'externalGuarantees']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.riskManagement.fields.externalGuarantees.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.riskManagement.fields.externalGuarantees.placeholder'
              )}
            >
              <Option value="none">
                {t(
                  'project:taskContent.riskManagement.fields.externalGuarantees.options.none'
                )}
              </Option>
              <Option value="few">
                {t(
                  'project:taskContent.riskManagement.fields.externalGuarantees.options.few'
                )}
              </Option>
              <Option value="many">
                {t(
                  'project:taskContent.riskManagement.fields.externalGuarantees.options.many'
                )}
              </Option>
              <Option value="high_risk">
                {t(
                  'project:taskContent.riskManagement.fields.externalGuarantees.options.highRisk'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t(
              'project:taskContent.riskManagement.fields.assetMortgageStatus.label'
            )}
            name={['riskManagement', 'assetMortgageStatus']}
            rules={[
              {
                required: true,
                message: t(
                  'project:taskContent.riskManagement.fields.assetMortgageStatus.required'
                ),
              },
            ]}
          >
            <Select
              placeholder={t(
                'project:taskContent.riskManagement.fields.assetMortgageStatus.placeholder'
              )}
            >
              <Option value="none">
                {t(
                  'project:taskContent.riskManagement.fields.assetMortgageStatus.options.none'
                )}
              </Option>
              <Option value="few">
                {t(
                  'project:taskContent.riskManagement.fields.assetMortgageStatus.options.few'
                )}
              </Option>
              <Option value="many">
                {t(
                  'project:taskContent.riskManagement.fields.assetMortgageStatus.options.many'
                )}
              </Option>
              <Option value="high_ratio">
                {t(
                  'project:taskContent.riskManagement.fields.assetMortgageStatus.options.highRatio'
                )}
              </Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Content style={{ padding: '24px' }}>
        {/* 页面头部 */}
        <div style={{ marginBottom: '24px' }}>
          <Breadcrumb style={{ marginBottom: '16px' }}>
            <Breadcrumb.Item>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() =>
                  navigate(`/admin/project-management/projects/${projectCode}`)
                }
                style={{ padding: 0 }}
              >
                {t('project:detail.title')}
              </Button>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {t('assessment:breadcrumb.detailedAssessment')}
            </Breadcrumb.Item>
          </Breadcrumb>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div>
              <Title level={2} style={{ margin: 0, color: '#1f2937' }}>
                {t('assessment:page.questionnaireTitle')}
              </Title>
              <Text type="secondary" style={{ fontSize: '16px' }}>
                {t('assessment:page.projectCodeAndEta', {
                  projectCode,
                  eta: '30-35',
                })}
              </Text>
            </div>
            <Space>
              <Text type="secondary">
                {lastSavedAt &&
                  t('assessment:page.lastSavedAt', {
                    time: lastSavedAt.toLocaleTimeString(
                      isZh ? 'zh-CN' : 'en-US'
                    ),
                  })}
              </Text>
            </Space>
          </div>

          {/* 进度条 */}
          <Card size="small">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ flex: 1, marginRight: '24px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                  }}
                >
                  <Text strong>
                    {t('assessment:page.overview.overallCompletion')}
                  </Text>
                  <Text strong style={{ color: '#10b981' }}>
                    {completionProgress}%
                  </Text>
                </div>
                <Progress
                  percent={completionProgress}
                  strokeColor="#10b981"
                  showInfo={false}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ClockCircleOutlined
                  style={{ marginRight: '4px', color: '#6b7280' }}
                />
                <Text type="secondary">
                  {t('assessment:page.remainingEstimate', {
                    minutes: remainingMinutes,
                  })}
                </Text>
              </div>
            </div>
          </Card>
        </div>

        <Row gutter={[24, 0]}>
          {/* 左侧导航 */}
          <Col span={6}>
            <Affix offsetTop={100}>
              <Card title={t('assessment:page.quickNavigation')} size="small">
                <Anchor
                  items={assessmentModules.map(module => ({
                    key: module.key,
                    href: `#${module.key}`,
                    title: (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 0',
                        }}
                      >
                        <div
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: module.color,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '8px',
                            fontSize: '12px',
                          }}
                        >
                          {module.icon}
                        </div>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>
                            {module.title}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {module.estimatedTime}
                          </div>
                        </div>
                      </div>
                    ),
                  }))}
                />

                <Divider />

                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    icon={<SaveOutlined />}
                    onClick={handleSaveDraft}
                    loading={isLoading}
                    block
                  >
                    {t('assessment:action.save')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSubmitAssessment}
                    loading={isLoading}
                    block
                  >
                    {t('assessment:action.submit')}
                  </Button>
                </Space>
              </Card>
            </Affix>
          </Col>

          {/* 右侧表单内容 */}
          <Col span={18}>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={handleFormChange}
              scrollToFirstError
            >
              {/* 智能提示 */}
              <Alert
                message={t('assessment:page.smartPrefillTitle')}
                description={t('assessment:page.smartPrefillDescription')}
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              {/* 各个评估模块 */}
              <div id="issuerAnalysis">{renderIssuerAnalysis()}</div>

              <div id="financialAnalysis">{renderFinancialAnalysis()}</div>

              <div id="greenProjectAssessment">
                {renderGreenProjectAssessment()}
              </div>

              <div id="issuancePlan">{renderIssuancePlan()}</div>

              <div id="serviceRequirements">{renderServiceRequirements()}</div>

              <div id="riskManagement">{renderRiskManagement()}</div>

              {/* 底部操作栏 */}
              <Card style={{ position: 'sticky', bottom: '24px', zIndex: 100 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <Text type="secondary">
                      {t('assessment:page.completionSummary', {
                        percent: completionProgress,
                        minutes: remainingMinutes,
                      })}
                    </Text>
                  </div>
                  <Space>
                    <Button
                      icon={<SaveOutlined />}
                      onClick={handleSaveDraft}
                      loading={isLoading}
                    >
                      {t('assessment:action.save')}
                    </Button>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleSubmitAssessment}
                      loading={isLoading}
                      size="large"
                    >
                      {t('assessment:action.submit')}
                    </Button>
                  </Space>
                </div>
              </Card>
            </Form>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default OptimizedDetailedAssessment;
